import React, { useState, useEffect, useCallback } from 'react';
import { formatDateLocale } from '../../utils/dateFormat';
import { buildProviderTaxPayload } from '../../utils/providerTax';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, Loader2, Clock, Send, Check, X, AlertTriangle,
  DollarSign, Calendar, FileText, MessageCircle, User, Zap, ChevronDown
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import api from '../../utils/api';
import OfferComposer from '../../components/OfferComposer';
import { SalesStageBadge } from '../../components/leads/SalesStage';
import { formatOfferError } from '../../utils/currencyPolicy';


// B2 CLOSURE — inbox tabs are mutually-exclusive OPERATIONAL buckets derived purely from
// the canonical human_stage projection (single-valued per request ⇒ union == All, zero
// overlap, no persisted status/authority). Business meaning for the Clinic Admin's job:
//   Needs Response = new incoming demand not yet worked (human_stage NEW)
//   In Progress    = clinic is working it, incl. offer sent / declined / EXPIRED awaiting
//                    revision and paused (human_stage WORKING or ON_HOLD)
//   Converted      = sale accepted/won (human_stage WON)
//   Closed         = no longer open (human_stage LOST)
// An OPEN request whose latest Offer is EXPIRED or REJECTED is human_stage WORKING, so it
// stays In Progress — never "Closed" — matching the established business rule.
const INBOX_BUCKET = (r) => {
  const s = r.human_stage;
  if (s === 'NEW') return 'NEEDS_RESPONSE';
  if (s === 'WORKING' || s === 'ON_HOLD') return 'IN_PROGRESS';
  if (s === 'WON') return 'CONVERTED';
  if (s === 'LOST') return 'CLOSED';
  return 'UNCLASSIFIED'; // only if backend fails to project a stage (kept visible under All)
};

function SlaIndicator({ hours }) {
  if (hours == null) return null;
  const urgent = hours > 48;
  const warning = hours > 24;
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${urgent ? 'bg-red-50 text-red-600' : warning ? 'bg-amber-50 text-amber-600' : 'bg-slate-50 text-slate-500'}`}>
      <Clock className="h-2.5 w-2.5 inline mr-0.5" />
      {hours < 1 ? '<1h' : `${Math.round(hours)}h`}
    </span>
  );
}


// U3 — TREATX-DEAD-CODE-CLEANUP-01 (2026-02): legacy RespondModal removed.
// Replacement: OfferComposer is the sole rich offer-creation UI (see render
// block at the bottom of this file). The `/respond` backend contract is
// unchanged and remains the canonical writer via offer_service.create_offer.


export default function ClinicQuoteRequests() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('ALL');
  const [repFilter, setRepFilter] = useState('ALL');
  const [respondTo, setRespondTo] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // P7Z-3C-A (Bug A): use the api client (axios) so the request hits the
  // backend baseURL (/api prefix is auto-applied) and so auth is injected
  // by the existing interceptor. Read response.data.requests (axios shape).
  // Failures must NOT be silently swallowed — surface a toast.
  // P7Z-3C-A (Bug G): always fetch the unfiltered list — the active filter
  // is applied client-side so the "Responded" tab can bucket both
  // RESPONDED and QUOTE_SENT without any backend change.
  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/clinic/quote-requests');
      const d = res.data || {};
      setRequests(d.requests || []);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || t('provider.quoteRequests.loadFailed');
      toast.error(detail);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  // U2 / TREATX-FASTFIX-QUOTES-RESPOND-OPENS-OFFER-COMPOSER-01 (2026-06-12) +
  // U3 / TREATX-DEAD-CODE-CLEANUP-01 (2026-02): OfferComposer is the SOLE
  // rich offer-creation UI for both Sales Rep AND Clinic Admin Quotes; the
  // legacy lean RespondModal has been removed. `/respond` endpoint contract
  // preserved verbatim — both entry points converge on offer_service.create_offer
  // (the single canonical writer); `quotes` remains the sole SSOT.
  const handleRespond = async (leadId, offerData) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const validDays = parseInt(offerData.validUntil, 10);
      const inclusions = (offerData.includedItems || [])
        .filter(i => i?.included !== false && (i?.name || '').trim())
        .map(i => i.name.trim());
      const offerItems = (offerData.additionalServices || [])
        .filter(s => (s?.name || '').trim())
        .map(s => ({
          item_type: (s.name || '').slice(0, 50),
          description: '',
          price: parseFloat(s.price) || 0,
          currency: offerData.currency,
          // TREATX-BATCH-10C: preserve the composer's quantity so 5-night
          // hotel etc. survives the quote_response write-door instead of
          // rendering as a single cell downstream.
          quantity: parseInt(s.quantity, 10) || 1,
        }));
      const payload = {
        treatment_type: offerData.treatment || '',
        base_price: parseFloat(offerData.basePrice) || 0,
        currency: offerData.currency,  // PHASE 2B/B1 — backend policy validates; no client default
        // TREATX-TAX-5 — provider-declared treatment VAT (null = not declared).
        provider_tax: buildProviderTaxPayload(offerData),
        inclusions,
        valid_days: Number.isFinite(validDays) && validDays > 0 ? validDays : 30,
        discount_percentage: offerData.discountPercentage !== '' && offerData.discountPercentage != null
          ? parseFloat(offerData.discountPercentage) || 0
          : undefined,
        notes: offerData.notes || '',
        // UAT-FIN-OFFER-SEND-COERCION — explicit financial terms (missing ≠ zero); backend validates.
        deposit_type: offerData.depositType || 'percentage',
        deposit_value: Number.isFinite(Number(offerData.depositValue)) && offerData.depositValue !== '' ? Number(offerData.depositValue) : null,
        payment_structure: offerData.paymentStructure || 'single',
        installment_count: offerData.paymentStructure === 'installments' ? Number(offerData.installmentCount) : null,
        additional_services: (offerData.additionalServices || []).map(s => ({ name: s.name || '', price: parseFloat(s.price) || 0, quantity: parseInt(s.quantity, 10) || 1 })),
        offer_items: offerItems,
        template_id: offerData.templateId || null,
        campaign_id: offerData.campaignId || null,
      };
      const res = await api.post(`/clinic/quote-requests/${leadId}/respond`, payload);
      const d = res.data || {};
      if (d.success) {
        toast.success(t('provider.quoteRequests.quoteSent'));
        setRespondTo(null);
        await fetchRequests();
      } else {
        toast.error(d.detail || d.message || t('provider.quoteRequests.sendFailed'));
      }
    } catch (e) {
      const data = e?.response?.data;
      let detail = data?.detail || data?.message || e?.message || t('provider.quoteRequests.sendFailed');
      if (['CURRENCY_NOT_ALLOWED', 'CURRENCY_POLICY_UNAVAILABLE', 'CURRENCY_REQUIRED'].includes(data?.error)) {
        detail = formatOfferError(e, t, detail);
      }
      if (Array.isArray(data?.detail)) {
        detail = data.detail
          .map(x => {
            const field = Array.isArray(x?.loc) ? x.loc.filter(p => p !== 'body').join('.') : '';
            const msg = x?.msg || 'invalid';
            return field ? `${field}: ${msg}` : msg;
          })
          .join(' · ');
      }
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  // B2 CLOSURE — client-side row filter using the mutually-exclusive human_stage bucket.
  const _matches = (r) => {
    // Inbox operational bucket (mutually exclusive, derived from human_stage).
    if (filter && INBOX_BUCKET(r) !== filter) return false;
    // B2 — human sales-stage filter (canonical projection).
    if (stageFilter !== 'ALL' && r.human_stage !== stageFilter) return false;
    // B2 — assigned sales rep filter.
    if (repFilter !== 'ALL' && (r.assigned_agent_id || '') !== repFilter) return false;
    // B2 — search over patient / treatment.
    const q = search.trim().toLowerCase();
    if (q) {
      const hay = `${r.patient_name || ''} ${r.treatment_display_name || r.treatment_interest || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  };
  const visibleRequests = requests.filter(_matches);

  // B2 — assigned-rep options from the loaded dataset (no extra requests). Label is always a
  // human display name; a raw usr-<id> is never shown (falls back to a neutral label).
  const repOptions = React.useMemo(() => {
    const m = new Map();
    requests.forEach(r => {
      if (!r.assigned_agent_id) return;
      const nm = (r.assigned_agent_name || '').trim();
      const label = nm && !/^usr-/i.test(nm) ? nm : t('provider.quoteRequests.unnamedRep');
      m.set(r.assigned_agent_id, label);
    });
    return Array.from(m.entries()).sort((a, b) => String(a[1]).localeCompare(String(b[1])));
  }, [requests, t]);
  const HUMAN_STAGES = ['NEW', 'WORKING', 'ON_HOLD', 'WON', 'LOST'];

  // B2 CLOSURE — bucket counts (mutually exclusive; union == All).
  const counts = {
    all: requests.length,
    NEEDS_RESPONSE: requests.filter(r => INBOX_BUCKET(r) === 'NEEDS_RESPONSE').length,
    IN_PROGRESS: requests.filter(r => INBOX_BUCKET(r) === 'IN_PROGRESS').length,
    CONVERTED: requests.filter(r => INBOX_BUCKET(r) === 'CONVERTED').length,
    CLOSED: requests.filter(r => INBOX_BUCKET(r) === 'CLOSED').length,
    UNCLASSIFIED: requests.filter(r => INBOX_BUCKET(r) === 'UNCLASSIFIED').length,
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="quote-requests-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/clinic/dashboard')} data-testid="qr-back-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> {t('provider.common.backToDashboard')}
            </Button>
            <h1 className="text-xl font-semibold text-[#122033]">{t('provider.quoteRequests.title')}</h1>
            {counts.NEEDS_RESPONSE > 0 && <Badge className="bg-blue-100 text-blue-700 text-[10px]">{t('provider.quoteRequests.newBadge', { n: counts.NEEDS_RESPONSE })}</Badge>}
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-4" data-testid="qr-filters">
          {[
            { key: '', label: t('provider.quoteRequests.filterAll', { n: counts.all }), tip: null },
            { key: 'NEEDS_RESPONSE', label: t('provider.quoteRequests.tabNeedsResponse', { n: counts.NEEDS_RESPONSE }), tip: t('provider.quoteRequests.tipNeedsResponse') },
            { key: 'IN_PROGRESS', label: t('provider.quoteRequests.tabInProgress', { n: counts.IN_PROGRESS }), tip: t('provider.quoteRequests.tipInProgress') },
            { key: 'CONVERTED', label: t('provider.quoteRequests.tabConverted', { n: counts.CONVERTED }), tip: t('provider.quoteRequests.tipConverted') },
            { key: 'CLOSED', label: t('provider.quoteRequests.tabClosed', { n: counts.CLOSED }), tip: t('provider.quoteRequests.tipClosed') },
          ].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              title={f.tip || undefined}
              className={`px-3 py-1.5 rounded-full text-xs transition-all border ${
                filter === f.key ? 'bg-[#122033] text-white border-[#122033]' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
              }`} data-testid={`qr-filter-${f.key || 'all'}`}>
              {f.label}
            </button>
          ))}
        </div>
        {filter === 'IN_PROGRESS' && (
          <p
            className="text-[11px] text-slate-500 -mt-2 mb-3"
            data-testid="qr-inprogress-helper"
          >
            {t('provider.quoteRequests.inProgressHelper')}
          </p>
        )}

        {/* B2 — minimum scale controls: search + human-stage + assigned rep (client-side over the loaded list) */}
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="qr-controls">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('provider.quoteRequests.searchPlaceholder')}
            className="flex-1 min-w-[180px] px-3 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3BA4C5]"
            data-testid="qr-search-input"
          />
          <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg bg-white" data-testid="qr-stage-filter">
            <option value="ALL">{t('provider.quoteRequests.allStages')}</option>
            {HUMAN_STAGES.map(s => <option key={s} value={s}>{t(`provider.leads.stage.${s}`)}</option>)}
          </select>
          <select value={repFilter} onChange={(e) => setRepFilter(e.target.value)} className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg bg-white" data-testid="qr-rep-filter">
            <option value="ALL">{t('provider.quoteRequests.allReps')}</option>
            {repOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
        ) : visibleRequests.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg border border-slate-200" data-testid="qr-empty">
            <FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-400">{filter ? t('provider.quoteRequests.emptyBucket') : t('provider.quoteRequests.empty')}</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="qr-list">
            {visibleRequests.map(req => {
              return (
                <div key={req.lead_id} className="bg-white border border-slate-200 rounded-lg px-4 py-3 flex items-center justify-between hover:border-slate-300 transition-colors"
                  data-testid={`qr-item-${req.lead_id}`}>
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                      <User className="h-4 w-4 text-slate-400" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-[#122033] truncate">{req.patient_name}</span>
                        {/* B2 — canonical human-stage badge + context (same authority as Lead Management). */}
                        <SalesStageBadge
                          transitions={{ human_stage: req.human_stage, sales_context: req.sales_context }}
                          fallbackLabel={t('provider.quoteRequests.statusNew')}
                        />
                        {req.assigned_agent_name && (
                          <span className="text-[10px] text-slate-400" data-testid={`qr-owner-${req.lead_id}`}>· {req.assigned_agent_name}</span>
                        )}
                        {req.campaign_attribution && (
                          <Badge
                            variant="outline"
                            className="text-[9px] px-1.5 border-amber-300 text-amber-700 bg-amber-50"
                            title={[
                              t('provider.quoteRequests.campaignLabel', { title: req.campaign_attribution.campaign_title || req.campaign_attribution.campaign_id }),
                              req.campaign_attribution.treatment_type ? t('provider.quoteRequests.treatmentLabel', { name: req.campaign_attribution.treatment_type }) : null,
                              req.campaign_attribution.discount_value != null
                                ? t('provider.quoteRequests.discountLabel', { value: `${req.campaign_attribution.discount_value}${req.campaign_attribution.discount_type === 'percentage' ? '%' : ''}` })
                                : null,
                              req.campaign_attribution.valid_until ? t('provider.quoteRequests.validUntilLabel', { date: formatDateLocale(req.campaign_attribution.valid_until) }) : null,
                            ].filter(Boolean).join('\n')}
                            data-testid={`qr-campaign-attribution-${req.lead_id}`}
                          >
                            {t('provider.quoteRequests.campaignContext')}
                          </Badge>
                        )}
                        {(req.renewal_count > 0 || (req.request_cycle || 1) > 1) && (
                          <Badge
                            variant="outline"
                            className="text-[9px] px-1.5 border-sky-300 text-sky-700 bg-sky-50"
                            title={t('provider.quoteRequests.renewedTitle', {
                              cycle: req.request_cycle || 1,
                              date: formatDateLocale(req.renewed_at || req.current_cycle_started_at),
                            })}
                            data-testid={`qr-renewed-${req.lead_id}`}
                          >
                            {t('provider.quoteRequests.renewedBadge', { cycle: req.request_cycle || 1 })}
                          </Badge>
                        )}
                        <SlaIndicator hours={req.sla_hours} />
                      </div>
                      <p className="text-[11px] text-slate-400 truncate">
                        {req.treatment_display_name || req.treatment_interest || t('provider.quoteRequests.generalInquiry')} · {req.preferred_contact} · {formatDateLocale(req.created_at)}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 ml-3 shrink-0">
                    {req.quote && (
                      <span className="text-[10px] text-emerald-600 font-medium" data-testid={`quote-request-total-${req.lead_id}`}>{req.quote.currency} {(req.quote.financial_summary?.total ?? 0).toLocaleString()}</span>
                    )}
                    {req.status === 'NEW' && (
                      <Button size="sm" className="h-7 text-xs bg-[#3BA4C5] hover:bg-[#2d8aa8] text-white gap-1"
                        onClick={() => setRespondTo(req)} data-testid={`qr-respond-${req.lead_id}`}>
                        <Send className="h-3 w-3" /> {t('provider.quoteRequests.respond')}
                      </Button>
                    )}
                    {/* B2 — WORKING leads (declined/expired/sent latest offer) get a canonical
                        revise/new-offer action via the SAME OfferComposer writer — no dead ends,
                        no new mutation. WON shows a badge; LOST/ON_HOLD show no commercial action. */}
                    {req.human_stage === 'WORKING' && req.status !== 'NEW' && (
                      <Button variant="outline" size="sm" className="h-7 text-xs border-[#3BA4C5] text-[#3BA4C5] hover:bg-[#3BA4C5]/5 gap-1"
                        onClick={() => setRespondTo(req)} data-testid={`qr-revise-${req.lead_id}`}>
                        <Send className="h-3 w-3" /> {t('provider.quoteRequests.newOffer')}
                      </Button>
                    )}
                    {req.human_stage === 'WON' && (
                      <Badge className="bg-green-50 text-green-600 border-green-200 text-[10px]">
                        <Check className="h-2.5 w-2.5 mr-0.5" /> {t('provider.quoteRequests.acceptedBadge')}
                      </Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* U2 — TREATX-FASTFIX-QUOTES-RESPOND-OPENS-OFFER-COMPOSER-01 (2026-06-12) +
          U3 — TREATX-DEAD-CODE-CLEANUP-01 (2026-02): Clinic Admin "Respond" opens
          the canonical OfferComposer (same component used by Sales Rep). Single
          rich offer UI; single canonical writer (offer_service.create_offer).
          The legacy RespondModal has been removed. */}
      {respondTo && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
          data-testid="quotes-respond-offer-composer-overlay"
        >
          <div className="w-full max-w-3xl my-8 bg-white rounded-xl shadow-2xl">
            <div className="p-6 border-b flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold" data-testid="quotes-respond-offer-composer-title">{t('provider.quoteRequests.sendOffer')}</h2>
                <p className="text-gray-500 text-sm">
                  {t('provider.quoteRequests.sendOfferFor', { name: respondTo.patient_name, treatment: respondTo.treatment_display_name || respondTo.treatment_interest || t('provider.quoteRequests.generalInquiry') })}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setRespondTo(null)}
                data-testid="quotes-respond-offer-composer-close"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <OfferComposer
                lead={{
                  id: respondTo.lead_id,
                  treatment_interest: respondTo.treatment_interest,
                  patient_id: respondTo.patient_id,
                  // SSO-D — same patient-selection context as the rep surface.
                  campaign_attribution: respondTo.campaign_attribution || null,
                  preferred_doctor: respondTo.preferred_doctor || null,
                  selection_events: respondTo.selection_events || null,
                }}
                onOfferSent={(data) => handleRespond(respondTo.lead_id, data)}
                onCancel={() => setRespondTo(null)}
                enableTemplates
                enableCampaigns
                showPatientContext
                leadId={respondTo.lead_id}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
